import { Flags, type IFlagProps } from '@sede-enterprise/shell-ds-react-framework';
import type { FC } from 'react';
import { COUNTRY_HEALTH_DATA } from '../Dashboard/HealthCheck/constants';
import useMarketFilters from './useMarketFilters';

type Props = {
  selectedMarkets: string[];
  onChange: (markets: string[]) => void;
};

const ADDITIONAL_FLAG_CODES: Record<string, string> = {
  'czech republic': 'Cz',
  'united kingdom': 'Gb',
  ireland: 'Ie',
  poland: 'Pl',
};

const getFlagCode = (market: string) => {
  const name = market.trim().toLowerCase();
  return (
    ADDITIONAL_FLAG_CODES[name] ??
    COUNTRY_HEALTH_DATA.find((country) => country.name.toLowerCase() === name)?.flagCode
  );
};

const MarketButtonGroup = ({ selectedMarkets, onChange }: Props) => {
  const { markets, loading, error, retry } = useMarketFilters();
  const options = markets
    .map((name) => {
      const flagCode = getFlagCode(name);
      return { name, flagCode, label: flagCode?.toUpperCase() ?? name };
    })
    .sort(
      (a, b) =>
        Number(Boolean(b.flagCode)) - Number(Boolean(a.flagCode)) || a.label.localeCompare(b.label),
    );

  return (
    <fieldset className='market-button-group' aria-label='Markets' aria-busy={loading}>
      <button
        type='button'
        className='market-button'
        aria-label='All Markets'
        aria-pressed={selectedMarkets.length === 0}
        onClick={() => onChange([])}
      >
        <svg
          width='14'
          height='14'
          viewBox='0 0 16 16'
          fill='none'
          stroke='currentColor'
          aria-hidden='true'
        >
          <circle cx='8' cy='8' r='6' />
          <ellipse cx='8' cy='8' rx='3' ry='6' />
          <path d='M2 8h12M3 5h10M3 11h10' />
        </svg>
        All
      </button>
      {loading ? <output>Loading markets...</output> : null}
      {error ? (
        <>
          <span role='alert'>Unable to load market filters.</span>
          <button type='button' className='market-button' onClick={retry}>
            Retry markets
          </button>
        </>
      ) : null}
      {options.map(({ name, flagCode, label }) => {
        const Flag = flagCode ? (Flags as Record<string, FC<IFlagProps>>)[flagCode] : undefined;
        const selected = selectedMarkets.includes(name);
        return (
          <button
            key={name}
            type='button'
            className='market-button'
            title={name}
            aria-label={name}
            aria-pressed={selected}
            onClick={() =>
              onChange(
                selected
                  ? selectedMarkets.filter((market) => market !== name)
                  : [...selectedMarkets, name],
              )
            }
          >
            {Flag ? (
              <Flag height={14} width={14} />
            ) : (
              <span className='hierarchy-badge hierarchy-badge-market' aria-hidden='true'>
                M
              </span>
            )}
            {label}
          </button>
        );
      })}
    </fieldset>
  );
};

export default MarketButtonGroup;
